/**
 * Site content — the editable surface of the portfolio.
 *
 * This file holds what is structural or language-independent: ordering,
 * identifiers, links, figures, company names and technology names. Anything a
 * reader would want translated lives in `src/i18n/locales/<lang>/` and is
 * looked up by the `id` fields below.
 *
 * Ids are literal unions rather than `string` so a lookup like
 * `t(`work.cases.${c.id}.title`)` resolves to a known translation key; adding
 * an entry without its translation is then a compile error.
 *
 * To change what the site says, edit this file and the locale files, then
 * open a pull request. See docs/adr/0006-content-as-code.md.
 */

export type StatId = 'throughput' | 'database' | 'deploy' | 'engineers';
/**
 * The design gives the lead case a photograph and technology chips, and the
 * two supporting cases a closing line instead. They are separate shapes
 * rather than one optional-everything type, so a lookup like
 * `t(`work.cases.${c.id}.outcome`)` only resolves for the cases that have one.
 */
export type LeadCaseId = 'sg-wireless';
export type SupportingCaseId = 'withthegrid' | 'pycom';
export type CaseId = LeadCaseId | SupportingCaseId;
export type HabitId = 'measure' | 'boring' | 'slices' | 'team';
export type TimelineId = 'terramira' | 'withthegrid' | 'sg-wireless' | 'pycom' | 'iss';
export type BeyondId = 'mentoring' | 'writing' | 'languages' | 'education';
export type OfferId = 'architecture_review' | 'contract_lead' | 'full_time';
export type StepId = 'read' | 'answer' | 'call';

export interface Stat {
  id: StatId;
  /** Rendered verbatim — a figure, not prose. */
  value: string;
}

interface CaseBase {
  period: string;
  organisation: string;
}

export interface LeadCase extends CaseBase {
  id: LeadCaseId;
  image: { src: string; srcSet: string };
  /** Technology chips; product names, so not translated. */
  stack: readonly string[];
}

export interface SupportingCase extends CaseBase {
  id: SupportingCaseId;
}

export interface TimelineEntry {
  id: TimelineId;
  period: string;
  organisation: string;
  /** Marks the row rendered in the accent colour. */
  current: boolean;
}

export interface Offer {
  id: OfferId;
  /** The lead card is rendered dark in the design. */
  featured: boolean;
}

export interface Profile {
  name: string;
  initials: string;
  email: string;
  location: string;
  cvPath: string;
  hero: { src: string; srcSet: string };
  avatar: string;
  /** The portraits are the photographer's work; credited in the repository. */
  photographyCredit: string;
  social: readonly { id: string; href: string }[];
  stats: readonly Stat[];
  leadCase: LeadCase;
  supportingCases: readonly SupportingCase[];
  habits: readonly HabitId[];
  timeline: readonly TimelineEntry[];
  beyond: readonly BeyondId[];
  offers: readonly Offer[];
  steps: readonly StepId[];
}

export const profile: Profile = {
  name: 'Ahmad El Masri',
  initials: 'AEM',
  email: 'me@ahmadelmasri.com',
  location: 'Eindhoven',
  cvPath: '/Ahmad_El_Masri_-_Lead_Software_Engineer.pdf',

  hero: {
    src: '/portrait-hero.jpg',
    srcSet: '/portrait-hero-900.jpg 900w, /portrait-hero.jpg 1600w',
  },
  avatar: '/portrait-avatar.jpg',
  photographyCredit: 'Goos Hengeveld',

  social: [
    { id: 'github', href: 'https://github.com/elmasria' },
    { id: 'linkedin', href: 'https://linkedin.com/in/amasri' },
  ],

  stats: [
    { id: 'throughput', value: '1M/s' },
    { id: 'database', value: '40%' },
    { id: 'deploy', value: '1h' },
    { id: 'engineers', value: '20' },
  ],

  leadCase: {
    id: 'sg-wireless',
    period: '2023–24',
    organisation: 'SG Wireless',
    image: {
      src: '/portrait-case.jpg',
      srcSet: '/portrait-case-700.jpg 700w, /portrait-case.jpg 1200w',
    },
    stack: ['Node.js', 'MQTT', 'MongoDB', 'Autoscaling'],
  },

  supportingCases: [
    { id: 'withthegrid', period: '2024–26', organisation: 'Withthegrid' },
    { id: 'pycom', period: '2019–23', organisation: 'Pycom' },
  ],

  habits: ['measure', 'boring', 'slices', 'team'],

  timeline: [
    { id: 'terramira', period: '2026 —', organisation: 'Terramira', current: true },
    { id: 'withthegrid', period: '2024–26', organisation: 'Withthegrid', current: false },
    { id: 'sg-wireless', period: '2023–24', organisation: 'SG Wireless', current: false },
    { id: 'pycom', period: '2019–23', organisation: 'Pycom', current: false },
    { id: 'iss', period: '2015–19', organisation: 'ISS', current: false },
  ],

  beyond: ['mentoring', 'writing', 'languages', 'education'],

  offers: [
    { id: 'architecture_review', featured: true },
    { id: 'contract_lead', featured: false },
    { id: 'full_time', featured: false },
  ],

  steps: ['read', 'answer', 'call'],
};

/** Anchors used by the header navigation, in document order. */
export const SECTIONS = ['work', 'approach', 'about', 'hire'] as const;

export type SectionId = (typeof SECTIONS)[number];
